import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useKeyPress } from "../../hooks/KeyPress";
import CloseIcon from "../../icons/Close";
import { stopScroll } from "../../utils/modal";
import {
  Background,
  Body,
  CloseButton,
  Container,
  ErrorBand,
  Eyebrow,
  Footer,
  FooterLink,
  Header,
  Heading,
  type ModalButtonVariant,
  type ModalSize,
  PrimaryButton,
  SecondaryButton,
  Subtitle,
  Title,
} from "./styles";

type Props = {
  title?: React.ReactNode;
  /** Supporting line under the title */
  subtitle?: React.ReactNode;
  /** Above the title (e.g. the Keepo Plus tag) */
  eyebrow?: React.ReactNode;
  /** Without a title: the id of the heading in the body that names the dialog */
  labelledBy?: string;
  /** A click outside or Escape closes it. */
  outsideExit?: boolean;
  /** Only Escape (and the X) closes it: a click outside doesn't (e.g. a modal with work in it). */
  escapeExit?: boolean;
  onExit?: () => void;
  /** Saving/sending: nothing closes it (X, Escape, click outside) until it's done. */
  busy?: boolean;
  children?: React.ReactNode;
  /** The actions, in a bar at the bottom that stays in sight: a ModalSecondaryButton, then a ModalButton. */
  footer?: React.ReactNode;
  /** Shown above the footer, always in sight. */
  error?: React.ReactNode;
  /** sm 400px (confirmations), md 480px (default), lg 880px */
  size?: ModalSize;
  /** The body touches the frame's edges (image crop, color picker) */
  flush?: boolean;
  /** A line under the header, for a body that scrolls under it (e.g. a grid) */
  divided?: boolean;
  /** A Keepo Plus offer: green glow and border */
  tone?: "plus";
  /** No X (only the ConfirmModal, when nothing can close it): every other modal has it */
  hideExitButton?: boolean;
  /** The X's accessible name ("Fechar" by default) */
  exitLabel?: string;
  className?: string;
};

// The modals open now, the last one on top: only it answers Escape
const openModals: object[] = [];

/**
 * The editor's modal (same as Domains' "connect a Keepo"): a header with the title, a supporting line and the X, a
 * body that scrolls, an optional bar with the actions at the bottom. A sheet from the bottom on phones.
 */
const Modal = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    title,
    subtitle,
    eyebrow,
    labelledBy,
    outsideExit,
    escapeExit,
    onExit,
    busy,
    children,
    footer,
    error,
    size = "md",
    flush = false,
    divided = false,
    tone,
    hideExitButton,
    exitLabel,
    className,
  } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const plus = tone === "plus";

  const exit = () => !busy && onExit?.();

  const [self] = useState(() => ({}));
  useEffect(() => {
    openModals.push(self);
    return () => {
      openModals.splice(openModals.indexOf(self), 1);
    };
  }, [self]);

  useKeyPress("Escape", () => openModals[openModals.length - 1] === self && (outsideExit || escapeExit) && exit());

  // Locks the page scroll once while open (not on every render)
  useEffect(() => stopScroll(), []);

  // Focus goes into the modal (unless a field in it took it already) and back to what opened it on closing
  const container = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    if (container.current && !container.current.contains(document.activeElement)) {
      container.current.focus();
    }
    return () => {
      if (opener?.isConnected) {
        opener.focus();
      }
    };
  }, []);

  const setContainer = useCallback(
    (element: HTMLDivElement | null) => {
      container.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref]
  );

  // A click outside closes it only when the press started outside too (not a text selection dragged out of it)
  const pressedOutside = useRef(false);

  const hasHeading = !!title || !!subtitle || !!eyebrow;

  return (
    <Background
      $sheet
      className={className}
      onMouseDown={event => {
        pressedOutside.current = event.target === event.currentTarget;
      }}
      onClick={event => {
        if (outsideExit && pressedOutside.current && event.target === event.currentTarget) {
          exit();
        }
        pressedOutside.current = false;
      }}>
      {/* A click anywhere inside the modal (header included) isn't a click outside it */}
      <Container
        ref={setContainer}
        tabIndex={-1}
        className="modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : labelledBy}
        $size={size}
        $plus={plus}
        onClick={event => event.stopPropagation()}>
        {(hasHeading || !hideExitButton) && (
          <Header $divided={divided}>
            {hasHeading && (
              <Heading>
                {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                {title && <Title id={titleId}>{title}</Title>}
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
              </Heading>
            )}
            {!hideExitButton && (
              <CloseButton type="button" aria-label={exitLabel || t("actions.close")} disabled={busy} onClick={exit}>
                <CloseIcon width={10} height={10} />
              </CloseButton>
            )}
          </Header>
        )}
        <Body className="modal-body" $flush={flush} $divided={divided}>
          {children}
        </Body>
        {error && <ErrorBand role="alert">{error}</ErrorBand>}
        {footer && <Footer $plus={plus}>{footer}</Footer>}
      </Container>
    </Background>
  );
});

Modal.displayName = "Modal";

type ModalButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "disabled"> & {
  /** INFO (blue, default), DANGER (red), WHITE or plus (green, a Keepo Plus offer) */
  variant?: ModalButtonVariant;
  /** Saving: the Keepo loader in place of the label, not pressable */
  loading?: boolean;
  /** Nothing to do yet: grayed and not pressable, but still reachable with Tab */
  disabled?: boolean;
};

/** The footer's main action (Confirm, Save, Connect…), a pill */
export const ModalButton = ({ variant = "INFO", loading, disabled, onClick, children, ...props }: ModalButtonProps) => (
  <PrimaryButton
    type="button"
    $variant={variant}
    $loading={loading}
    aria-disabled={disabled || loading || undefined}
    aria-busy={loading || undefined}
    onClick={event => !disabled && !loading && onClick?.(event)}
    {...props}>
    {children}
  </PrimaryButton>
);

export { SecondaryButton as ModalSecondaryButton, FooterLink as ModalFooterLink };

export default Modal;
